/// <amd-module name="WorkItemsHub/Components/WorkItemsTabContent" />
import { IWorkItemsGridData, IWorkItemsGridRow } from "DataContracts/IWorkItemsGridData";
import { WorkItemsHubDataProvider } from "DataProviders/WorkItemsHubDataProvider";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import {
    CheckboxVisibility,
    ConstrainMode,
    DetailsListLayoutMode,
    DetailsRow,
    IColumn,
    IDetailsRowProps,
    SelectionMode
} from "OfficeFabric/DetailsList";
import { Selection } from "OfficeFabric/Selection";
import * as React from "react";
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { PerformanceTelemetryHelper } from "Utils/Telemetry";
import { IWorkItemsGridColumnFactory, WorkItemsGridColumnFactory } from "Utils/WorkItemsGridColumnFactory";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssContributionEventService } from "VSS/Platform/Contribution";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { VssDetailsList } from "VSSUI/Components/VssDetailsList/VssDetailsList";
import { IVssDetailsList } from "VSSUI/Components/VssDetailsList/VssDetailsList.Props";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { arrayEquals, css } from "VSSUI/Util";
import { FILTER_CHANGE_EVENT, IFilter } from "VSSUI/Utilities/Filter";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItemsHubNavData, IWorkItemsHubNavDataService } from "Wit/Common/WorkItemsHubNavDataService/IWorkItemsHubNavDataService";
import { isFilterStateEmpty } from "Wit/Filter/FilterManager";
import { generateDefaultFilterState, mapToFilterState, resolveFilterState } from "Wit/Filter/WorkItemFilter";
import { ActionsCreator } from "WorkItemsHub/Actions/ActionsCreator";
import { IWorkItemsTabContentData } from "WorkItemsHub/Components/IWorkItemsTabContentData";
import * as Constants from "WorkItemsHub/Constants";
import { WorkItemsGridDataProvider } from "WorkItemsHub/DataProviders/WorkItemsGridDataProvider";
import { WorkItemsHubCommandProvider } from "WorkItemsHub/DataProviders/WorkItemsHubCommandProvider";
import { WorkItemsHubPaging, WorkItemsHubTabs } from "WorkItemsHub/Generated/Constants";
import { WorkItemsHubData, WorkItemsHubSortOption } from "WorkItemsHub/Generated/Contracts";
import * as Resources from "WorkItemsHub/Resources";
import { WorkItemsHubStore } from "WorkItemsHub/Stores/WorkItemsHubStore";
import { findIndex } from "WorkItemsHub/Utils/Array";
import { IDisposable } from "WorkItemsHub/Utils/Common";
import { UsageTelemetryHelper } from "WorkItemsHub/Utils/Telemetry";
import { updateColumnSettings } from "WorkItemsHub/Utils/WorkItemsHubTabSettings";
import * as WorkItemsHubTabUtils from "WorkItemsHub/Utils/WorkItemsHubTabUtils";
import * as WorkItemsXhrNavigationUtils from "WorkItemsHub/Utils/WorkItemsXhrNavigationUtils";
import { ZeroDataFactory } from "WorkItemsHub/ZeroData/ZeroDataFactory";
import { WorkItemsHubViewProvider } from "WorkItemsHubViewProvider";

export interface IWorkItemsTabContentProps extends IVssComponentProperties {
    tabId: string;
    tabName: string;
}

export interface IWorkItemsTabContentState {
    initialized: boolean;
    selection: Selection;
    gridData: IWorkItemsGridData;
    columns: IColumn[];
    totalItemCount: number;
    visibleItemCount: number;
    isFiltered: boolean;
    isError: boolean;
    isSupportedFeature: boolean;
}

export class WorkItemsTabContent extends VssComponent<IWorkItemsTabContentProps, IWorkItemsTabContentState> {
    private static readonly ItemHeight = 41; // Height of a single item in the list
    private static readonly DetailsListPageSize = 10; // Number of items in a list page.
    private static readonly WorkItemStateIndex = 3; // Index of state index in values array of work item grid data

    private static ColumnResizeDefaultDelayTimeInMilliseconds = 500;

    private _selectedIds: number[] = [];
    private _telemetrySent = false;
    private _vssDetailsList: IVssDetailsList;
    private _initialSelectedIndex: number;
    private _recreateColumns: boolean = false;
    private _delayedOnColumnResize: (column?: IColumn, newWidth?: number, columnIndex?: number) => void;
    private _timerManagement: TimerManagement;

    private _store: WorkItemsHubStore;
    private _actionsCreator: ActionsCreator;
    private _filter: IFilter;
    private _project: IProjectData;
    private _gridColumnFactory: IWorkItemsGridColumnFactory;
    private _perfTelemetryHelper: PerformanceTelemetryHelper;
    private _scrollableContentContainer: Element;
    private _lastContentData: IWorkItemsTabContentData;
    private _workItemsHubCommandProvider: WorkItemsHubCommandProvider;
    private _workItemEventHandler: IDisposable;
    private _isDataCached: boolean = false;

    constructor(props?: IWorkItemsTabContentProps, context?: any) {
        super(props, context);

        this._store = WorkItemsHubViewProvider.getStore();
        this._actionsCreator = WorkItemsHubViewProvider.getActionsCreator();
        this._filter = WorkItemsHubViewProvider.getHubViewState().filter;
        this._workItemsHubCommandProvider = WorkItemsHubViewProvider.getWorkItemsHubCommandProvider();

        this._gridColumnFactory = new WorkItemsGridColumnFactory();
        this._perfTelemetryHelper = PerformanceTelemetryHelper.getInstance(this.context.pageContext);

        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        this._project = tfsPageData.project;

        this._timerManagement = new TimerManagement(this);
        this._delayedOnColumnResize = this._timerManagement.debounce(
            this._onColumnResize,
            WorkItemsTabContent.ColumnResizeDefaultDelayTimeInMilliseconds,
            {
                leading: false,
                trailing: true
            }
        );

        this.state = this.getState();
    }

    public render(): JSX.Element {
        const { initialized, totalItemCount, visibleItemCount, gridData, isError, isSupportedFeature, isFiltered } = this.state;
        const { tabId } = this.props;
        let content: JSX.Element | JSX.Element[];
        if (isError) {
            content = ZeroDataFactory.createForMissingTabData(this.context.pageContext);
        } else if (!initialized) {
            content = <Spinner size={SpinnerSize.large} className="work-items-hub-spinner" label={Resources.Loading} />;
        } else if (visibleItemCount === 0) {
            if (isFiltered) {
                announce(Utils_String.format(Resources.MultipleWorkItemsFiltered, visibleItemCount));
            }
            content = ZeroDataFactory.createForEmptyData(tabId, isSupportedFeature, totalItemCount > 0, this.context.pageContext);
        } else {
            if (isFiltered) {
                announce(
                    visibleItemCount == 1
                        ? Utils_String.format(Resources.SingleWorkItemFiltered, visibleItemCount)
                        : Utils_String.format(Resources.MultipleWorkItemsFiltered, visibleItemCount)
                );
            }
            content = (
                <div role="navigation" aria-label={Resources.WorkItemsHubGridLandmarkLabel}>
                    {this._buildDetailList(gridData)}
                </div>
            );
        }

        return <div className={Constants.TabContent_BaseClassName}>{content}</div>;
    }

    private _buildDetailList(gridData: IWorkItemsGridData): JSX.Element {
        const { columns, selection, isFiltered } = this.state;
        const getMenuItems = (item: IWorkItemsGridRow): IContextualMenuItem[] =>
            this._workItemsHubCommandProvider.getContextMenuItems(this.context.pageContext, this._store.getWorkItemsHubPermission(), item);

        return (
            <VssDetailsList
                className={css(Constants.TabContent_WorkItemsHubDetailsListClassName, `${this.props.tabId}-grid`)}
                usePresentationStyles={true}
                items={gridData}
                columns={columns}
                isHeaderVisible={true}
                ariaLabelForSelectionColumn={Resources.SelectAllAriaLabel}
                onColumnHeaderClick={this._onColumnClick}
                onColumnResize={this._delayedOnColumnResize}
                constrainMode={ConstrainMode.unconstrained}
                selectionMode={SelectionMode.multiple}
                checkboxVisibility={CheckboxVisibility.onHover}
                checkButtonAriaLabel={Resources.CheckButtonLabel}
                actionsColumnKey={CoreFieldRefNames.Title}
                getMenuItems={getMenuItems}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                selection={selection}
                onItemInvoked={this._handleNavigateToEdit}
                listProps={{
                    getPageHeight: this._getPageHeight,
                    getItemCountForPage: this._getItemCountForPage,
                    renderedWindowsAhead: isFiltered ? 0 : undefined,
                    renderedWindowsBehind: isFiltered ? 0 : undefined
                }}
                onRenderRow={this._onRenderRow}
                onRowDidMount={this._onRowDidMount}
                setKey={Constants.TabContent_GridBaseClassName}
                initialFocusedIndex={this._initialSelectedIndex}
                componentRef={this._onDetailsListRef}
            />
        );
    }

    private _onColumnClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn): void => {
        const shiftClick = ev.shiftKey;
        let newSortOptions: WorkItemsHubSortOption[];

        const fieldReferenceName = column.key;
        const store = this.getStore();
        const dataSource = store.getHubFilterDataSource(this.props.tabId);

        // No-op if this is an unsortable field
        if (!dataSource.isSortableField(fieldReferenceName)) {
            return;
        }

        // Get current sorting values
        const oldSortOptions = dataSource.getSortOptions();
        const oldIndex = oldSortOptions.findIndex(option => option.fieldReferenceName === fieldReferenceName);

        if (!shiftClick) {
            // clear any secondary sort choices
            // flip choice of ascending/descending if the column is already being sorted
            const isAscending = oldIndex < 0 || !oldSortOptions[oldIndex].isAscending;
            newSortOptions = [
                {
                    fieldReferenceName,
                    isAscending
                } as WorkItemsHubSortOption
            ];
        } else {
            // if column is already being sorted, flip its direction
            // otherwise, add as a new sorted column
            newSortOptions = oldSortOptions.slice();
            if (oldIndex < 0) {
                newSortOptions.push({
                    fieldReferenceName,
                    isAscending: true
                } as WorkItemsHubSortOption);
            } else {
                newSortOptions[oldIndex].isAscending = !oldSortOptions[oldIndex].isAscending;
            }
        }

        this._actionsCreator.sortColumn(this.props.tabId, newSortOptions);

        // get existing column display
        const columnOptions = dataSource.getCurrentColumnOptions();

        UsageTelemetryHelper.publishColumnHeaderClick(this.context.pageContext, this.props.tabId, {
            sortedColumnCount: newSortOptions.length,
            sortedColumns: newSortOptions.map(option => option.fieldReferenceName).join(),
            sortedOrder: newSortOptions.map(option => option.isAscending).join()
        });

        const version: number = dataSource.getCurrentColumnSettingsVersion();
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const projectId = tfsPageData.project.id;

        updateColumnSettings(this.context.pageContext, projectId, this.props.tabId, columnOptions, newSortOptions, version);

        // build new columns
        this._recreateColumns = true;
    };

    private _onColumnResize = (column?: IColumn, newWidth?: number): void => {
        // Get current column settings
        const fieldReference = column.key;
        const store = this._store;
        const dataSource = store.getHubFilterDataSource(this.props.tabId);
        const columnOptions = dataSource.getCurrentColumnOptions();
        const sortOptions = dataSource.getSortOptions();

        // Always save width as an integer value
        newWidth = Math.ceil(newWidth);

        columnOptions.forEach(option => {
            if (Utils_String.equals(option.fieldReferenceName, fieldReference, true)) {
                option.width = newWidth;
            }
        });

        UsageTelemetryHelper.publishColumnResize(this.context.pageContext, this.props.tabId, {
            columnName: fieldReference,
            width: newWidth
        });

        const version: number = dataSource.getCurrentColumnSettingsVersion();
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const projectId = tfsPageData.project.id;

        // save new column size
        updateColumnSettings(this.context.pageContext, projectId, this.props.tabId, columnOptions, sortOptions, version);
    };

    private _onDetailsListRef = (ref: IVssDetailsList): void => {
        this._vssDetailsList = ref;
    };

    private _handleNavigateToEdit = (item: IWorkItemsGridRow) => {
        return this._onOpenWorkItem(item.id);
    };

    private _onOpenWorkItem = (workItemId: number, ev?: React.MouseEvent<HTMLAnchorElement>): boolean | void => {
        if (!ev) {
            const startingWorkItemId = this._initTriageData(workItemId);
            WorkItemsXhrNavigationUtils.navigateToWorkEditWorkItemForm(this.context.pageContext, this._project.name, startingWorkItemId);
            return;
        }

        const startingWorkItemId = this._initTriageData(workItemId, true /* mouse event */);
        const result = WorkItemsXhrNavigationUtils.navigateToWorkEditWorkItemForm(
            this.context.pageContext,
            this._project.name,
            startingWorkItemId,
            ev
        );
        if (result) {
            // XHR navigating, so tell browser not to handle
            ev.preventDefault();
            ev.stopPropagation();
        }
    };

    /**
     * Initializes triage data.
     * @param workItemId Starting work item ID
     * @param isFromMouseEvent True if triaging from mouse event
     * @returns Actual starting work item ID based on selections
     */
    private _initTriageData(workItemId: number, isFromMouseEvent: boolean = false): number {
        if (this._lastContentData) {
            const { tabId, selectionIds } = this._lastContentData;
            const tabName = this.props.tabName;
            let workItemIds = selectionIds;
            let scrollTopValue: number = this._scrollableContentContainer ? this._scrollableContentContainer.scrollTop : 0;

            const dataSource = this._store.getHubFilterDataSource(tabId);
            const filteredIds: number[] = dataSource ? dataSource.getFilteredIds() : null;

            if (!selectionIds || selectionIds.length < 2) {
                if (filteredIds) {
                    workItemIds = filteredIds;
                }
            } else if (!isFromMouseEvent) {
                // When triaging based on selection, start with the first item.
                // Feature flag check here because w/o triage mode, we should navigate to the original target item.
                workItemId = selectionIds[0];
            }

            if (!workItemIds || workItemIds.length === 0) {
                this._setWorkItemsHubNavigationData(null);
                return workItemId;
            }

            if ((selectionIds && selectionIds.length === 0) || workItemIds.indexOf(workItemId) === -1) {
                // when nothing is selected or the target work item isn't in the triage list, fall back to the first item
                workItemId = workItemIds[0];
                scrollTopValue = 0;
            }

            // Get the index of the selected item from the full filtered list and not the workItemIds as it doesn't contain the full visible items list all the time
            const selectedItemIndexInVisibleList = findIndex(filteredIds, (filteredId: number) => filteredId === workItemId);
            this._setWorkItemsHubNavigationData({
                tabId,
                tabName,
                workItemIds,
                lastTriagedWorkItemId: workItemId,
                selectedItemIndexInVisibleList,
                selectedIndexScrollTopValue: scrollTopValue
            } as IWorkItemsHubNavData);
        } else {
            this._setWorkItemsHubNavigationData(null);
        }

        return workItemId;
    }

    private _getPageHeight = (): number => {
        return WorkItemsTabContent.ItemHeight * this._getItemCountForPage();
    };

    private _getItemCountForPage = (): number => {
        return WorkItemsTabContent.DetailsListPageSize;
    };

    private _onRenderRow = (props: IDetailsRowProps): JSX.Element => {
        return <DetailsRow {...props} className={Constants.TabContent_WorkItemsHubGridRowClassName} data-item-key={props.item.key} />;
    };

    private _onRowDidMount = (): void => {
        if (!this._vssDetailsList || !this._vssDetailsList.detailsList || !this.state.selection || this._initialSelectedIndex === -1) {
            return;
        }

        // end a performance scenario when row gets mounted as DetailsList doesn't get mounted in initial rendering
        this._tryEndPerformanceScenario();

        const { selection } = this.state;

        // This is the same technique used by DetailsList internally (to set focus on a row), we're
        // waiting for the first row mount and then scrolling to our target row and setting selection on it; we're only
        // scroll and select once (notice setting _initialSelectedIndex to -1 at the end and the condition above).
        const initialIndex = this._initialSelectedIndex;

        if (!this._trySetEffectiveScrollPosition(initialIndex)) {
            this._vssDetailsList.detailsList.scrollToIndex(initialIndex);
        }

        requestAnimationFrame(() => selection.setIndexSelected(initialIndex, true, true));

        this._initialSelectedIndex = -1;
    };

    public componentDidMount(): void {
        super.componentDidMount();
        this._performanceScenarioSplit("TabContentDidMount");

        this.getStore().addChangedListener(this._onStoreChange);

        const isDataInitialized = this._store.isHubDataInitialized(this.props.tabId);
        this._isDataCached = isDataInitialized;

        if (!isDataInitialized) {
            this._performanceScenarioSplit("FetchData");
            // This is pivot switch scenario. Data is not cached so fetch data with initial page size
            this._actionsCreator.refreshDataProviderAsync(this.props.tabId, false, WorkItemsHubPaging.InitialPageSize);
        } else if (this._filter) {
            this._setFilterForTab();
        }

        // end a performance scenario using timer as DetailsList doesn't get rendered in initial render
        // once we replace it with table component we can remove this workaround
        // this also covers a zero data scenario
        setTimeout(() => this._tryEndPerformanceScenario(), 0);

        this._scrollableContentContainer = document.querySelector("[data-is-scrollable='true']");

        this._loadLegacyEventHandlersAfterTTI(3000);
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this._unsubscribeFilter();

        this.getStore().removeChangedListener(this._onStoreChange);

        if (this._timerManagement) {
            this._timerManagement.dispose();
            this._timerManagement = null;
        }

        if (this._workItemEventHandler) {
            this._workItemEventHandler.dispose();
            this._workItemEventHandler = null;
        }
    }

    private _fetchMoreItems = () => {
        const { tabId } = this.props;
        const dataSource = this._store.getHubFilterDataSource(tabId);

        if (dataSource.canPageMoreItems()) {
            this._actionsCreator.pageWorkItems(tabId, dataSource);
        }
    };

    private _loadLegacyEventHandlers = () => {
        const legacyService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");

        if (!this._workItemEventHandler) {
            // load work item event handler
            legacyService.requireModules(["WorkItemsHub/Scripts/LegacyPlatform/LegacyWorkItemEventHandler"]).then(([module]) => {
                this._workItemEventHandler = new module.LegacyWorkItemEventHandler({
                    onSave: this._onSave,
                    onDelete: this._onDelete,
                    onFollowsStateChanged: this._onFollowsStateChanged
                });
            });
        }
    };

    private _loadLegacyEventHandlersAfterTTI(interactiveTimeout: number) {
        let pageInteractiveTimeout = this.setTimeout(() => {
            this._loadLegacyEventHandlers();
            this.clearTimeout(pageInteractiveTimeout);
        }, interactiveTimeout);

        const contributedEventService = this.context.pageContext.getService<IVssContributionEventService>("IVssContributionEventService");
        contributedEventService.subscribe(() => {
            if (pageInteractiveTimeout) {
                this._loadLegacyEventHandlers();
                this.clearTimeout(pageInteractiveTimeout);
            }
        }, "page-interactive");
    }

    private _onSave = () => {
        const currentTabId = WorkItemsHubDataProvider.getCurrentTabId(this.context.pageContext);
        this._actionsCreator.refreshDataProviderAsync(currentTabId, true);
    };

    private _onDelete = (workItemId: number) => {
        const currentTabId = WorkItemsHubDataProvider.getCurrentTabId(this.context.pageContext);
        this._actionsCreator.removeWorkItemFromDataProvider(currentTabId, [workItemId]);
    };

    private _onFollowsStateChanged = () => {
        const followingTabId = WorkItemsHubTabUtils.TabIdByTabEnumValueMap[WorkItemsHubTabs.Following];
        const currentTabId = WorkItemsHubDataProvider.getCurrentTabId(this.context.pageContext);
        if (currentTabId === followingTabId) {
            this._actionsCreator.refreshDataProviderAsync(currentTabId, true);
        } else {
            this._actionsCreator.invalidateTabData(followingTabId);
        }
    };

    private _filterChanged = () => {
        const { tabId } = this.props;

        this._actionsCreator.updateFilterState(tabId, mapToFilterState(this._filter.getState()));
        this._onTabContentChanged();
    };

    public componentWillUpdate(nextProps: IWorkItemsTabContentProps, nextState: IWorkItemsTabContentState) {
        // Cache selected keys so they can be restored after updating.
        const { selection } = this.state;
        this._selectedIds = selection.getSelectedCount() > 0 ? selection.getSelection().map((row: IWorkItemsGridRow) => row.id) : [];

        if (this.state.initialized && !nextState.initialized) {
            // Tab data was refreshed - unsubscribe from the filter.
            this._unsubscribeFilter();
        } else if (!this.state.initialized && nextState.initialized) {
            // Tab was loading before, now that it's setup, initialize the filter with the loaded filter state
            this._setFilterForTab();
        }
    }

    private _trySetEffectiveScrollPosition(initialIndex: number): boolean {
        let isEffectiveScrollPositionSet: boolean = false;

        // No point messing with the Math if current index of selected element is 0. This can happen quite frequently with 'My Activity' or 'Recently Updated' tabs.
        if (initialIndex !== 0) {
            const navigationData = this._getWorkItemsHubNavigationData();

            if (this._scrollableContentContainer && navigationData && navigationData.lastTriagedWorkItemId) {
                let effectiveScrollTopValue = navigationData.selectedIndexScrollTopValue;
                const oldIndexOfLastTriagedWorkItem = navigationData.selectedItemIndexInVisibleList;
                effectiveScrollTopValue -= (oldIndexOfLastTriagedWorkItem - initialIndex) * WorkItemsTabContent.ItemHeight;
                this._scrollableContentContainer.scrollTop = effectiveScrollTopValue;
                isEffectiveScrollPositionSet = true;
            }
        }

        return isEffectiveScrollPositionSet;
    }

    private _unsubscribeFilter() {
        const filter = this._filter;
        filter.unsubscribe(this._filterChanged, FILTER_CHANGE_EVENT);
    }

    private _setFilterForTab() {
        // Set persisted filter state
        const store = this.getStore();

        const { tabId } = this.props;
        const filter = this._filter;
        const filterFields = store.getHubFilterFields(tabId);
        const filterDataSource = store.getHubFilterDataSource(tabId);
        const filterState = filterDataSource ? filterDataSource.getFilterState() : null;

        filter.setDefaultState(generateDefaultFilterState(filterFields));
        filter.reset();

        if (!isFilterStateEmpty(filterState)) {
            resolveFilterState(filterFields, filterDataSource, filterState).then(
                resolvedFilterState => {
                    filter.setState(resolvedFilterState);
                    filter.subscribe(this._filterChanged, FILTER_CHANGE_EVENT);
                },
                () => {
                    // Ignore error, just clear out filter
                    filter.reset();
                }
            );
        } else {
            filter.subscribe(this._filterChanged, FILTER_CHANGE_EVENT);
        }

        this._onTabContentChanged();
    }

    public componentDidUpdate() {
        const { gridData, selection } = this.state;
        if (gridData) {
            selection.setItems(gridData, true);
            if (this._selectedIds.length > 0) {
                const indicesToSelect: number[] = [];
                gridData.forEach((row: IWorkItemsGridRow, index: number) => {
                    if (this._selectedIds.indexOf(row.id) > -1) {
                        indicesToSelect.push(index);
                    }
                });
                requestAnimationFrame(() => indicesToSelect.forEach((index: number) => selection.setIndexSelected(index, true, true)));
            }
        }

        this._tryEndPerformanceScenario();
    }

    private _buildColumns(hubData: WorkItemsHubData, sortOptions: WorkItemsHubSortOption[], columnFieldRefNames: string[]): IColumn[] {
        let columns: IColumn[] = null;

        if (!hubData) {
            return columns;
        }

        if (this.state && this.state.columns && !this._recreateColumns) {
            const currentFieldRefNames = this.state.columns.map(col => col.fieldName);
            if (arrayEquals(currentFieldRefNames, columnFieldRefNames)) {
                return this.state.columns;
            }
        }

        columns = WorkItemsGridDataProvider.buildColumns(
            this.context.pageContext,
            this._project.name,
            hubData,
            sortOptions,
            this._gridColumnFactory,
            new Date(),
            this._onOpenWorkItem
        );

        this._recreateColumns = false;

        return columns;
    }

    protected getState(): IWorkItemsTabContentState {
        const store: WorkItemsHubStore = this.getStore();
        const tabId: string = this.props.tabId;
        const initialized: boolean = store.isHubDataInitialized(tabId);
        const isError: boolean = store.isHubDataError(tabId);
        const isSupportedFeature: boolean = store.isSupportedFeature(tabId);
        const totalItemCount: number = store.getTotalItemsCount(tabId);
        const visibleItemCount: number = store.getVisibleItemsCount(tabId);
        const dataSource = store.getHubFilterDataSource(tabId);

        let sortOptions: WorkItemsHubSortOption[] = null;
        let columnFieldRefNames: string[] = null;
        let isFiltered = false;
        let hubData: WorkItemsHubData = null;
        let gridData: IWorkItemsGridData = null;

        if (dataSource) {
            sortOptions = dataSource.getSortOptions();
            columnFieldRefNames = dataSource.getDisplayedFieldReferenceNames();
            isFiltered = dataSource.hasFilter();
        }

        if (initialized) {
            hubData = store.getHubDisplayData(tabId);
            gridData = WorkItemsGridDataProvider.getData(hubData, isFiltered);
        }

        const selection = (this.state && this.state.selection) || new Selection({ onSelectionChanged: this._onTabContentChanged });

        const previousInitializedState: boolean = this.state && this.state.initialized;
        if (!previousInitializedState && gridData && gridData.length > 0) {
            let selectedIndex = 0;
            const navigationData = this._getWorkItemsHubNavigationData();
            if (navigationData && navigationData.lastTriagedWorkItemId != null) {
                selectedIndex = findIndex(gridData, (r: IWorkItemsGridRow) => r.id === navigationData.lastTriagedWorkItemId);
            }
            this._initialSelectedIndex = Math.max(0, selectedIndex);
        }

        const columns: IColumn[] = this._buildColumns(hubData, sortOptions, columnFieldRefNames);

        if (gridData !== null && localeIgnoreCaseComparer(tabId, WorkItemsHubTabs[WorkItemsHubTabs.AssignedToMe]) === 0) {
            gridData = gridData.filter(obj => obj.values[WorkItemsTabContent.WorkItemStateIndex] !== "Removed");
        }

        return {
            initialized,
            totalItemCount,
            visibleItemCount,
            gridData,
            columns,
            selection,
            isFiltered,
            isError,
            isSupportedFeature
        };
    }

    protected getStore(): WorkItemsHubStore {
        return this._store;
    }

    private _onStoreChange = () => {
        const newState = this.getState();
        this.setState(newState);
    };

    /**
     * This function should get called whenever tab content changes.
     */
    private _onTabContentChanged = () => {
        const { selection } = this.state;
        const { tabId } = this.props;
        const itemsSelection = selection.getSelection();
        const selectionIds = itemsSelection.map((row: IWorkItemsGridRow) => row.id);
        const store = this.getStore();
        const dataSource = store.getHubFilterDataSource(tabId);
        const hasFilter = dataSource ? dataSource.hasFilter() : false;

        const data: IWorkItemsTabContentData = { selectionIds, hasFilter, tabId };

        this._lastContentData = data;

        this._workItemsHubCommandProvider.setTabData(data);

        if (store) {
            WorkItemsHubViewProvider.updateCommandsActionsAndViewOptions(tabId);
        }
    };

    private _getWorkItemsHubNavigationData(): IWorkItemsHubNavData {
        const navigationDataService = this.context.pageContext.getService<IWorkItemsHubNavDataService>("IWorkItemsHubNavDataService");
        return navigationDataService.getNavigationData();
    }

    private _setWorkItemsHubNavigationData(data: IWorkItemsHubNavData): void {
        const navigationDataService = this.context.pageContext.getService<IWorkItemsHubNavDataService>("IWorkItemsHubNavDataService");
        navigationDataService.setNavigationData(data);
    }

    /**
     * Try to end a currently active performance scenario. This function is called at two places:
     * - DetailsList onRowDidMount : this is to ensure we are ending a scenario when detailslist is rendered
     * - componentDidMount  : this is for the initial load or switch to a tab that has cached data
     * - componentDidUpdate : this is for switching to a tab that doesn't have cached data
     */
    private _tryEndPerformanceScenario() {
        if (this._telemetrySent) {
            return;
        }

        const store: WorkItemsHubStore = this.getStore();
        const tabId: string = this.props.tabId;
        const isDataInitialized = store.isHubDataInitialized(tabId);

        if (isDataInitialized) {
            const dataSource = store.getHubFilterDataSource(tabId);
            const totalWorkItemsCount = dataSource ? dataSource.getItemCount() : -1;
            const pageItemsCount = dataSource ? dataSource.getPagedItemsCount() : -1;
            const visibleItemsCount = dataSource ? dataSource.getVisibleItemsCount() : -1;
            const hasFilters = dataSource ? dataSource.hasFilter() : false;
            const columnOptions = dataSource.getCurrentColumnOptions();
            const additionalProperties = {
                tabId: tabId,
                newWorkItemsHub: true,
                isDataCached: this._isDataCached,
                totalWorkItemsCount,
                pageItemsCount,
                visibleItemsCount,
                hasFilters: hasFilters,
                completedItems: dataSource.shouldShowCompletedItems(),
                columnCount: dataSource.getDisplayedFieldReferenceNames().length,
                columnNames: columnOptions.map(option => option.fieldReferenceName),
                columnWidths: columnOptions.map(option => option.width),
                sortedColumnCount: dataSource.getSortOptions().length,
                sortedColumnNames: dataSource.getSortOptions().map(option => option.fieldReferenceName)
            };

            this._perfTelemetryHelper.endScenario(additionalProperties);
            this._telemetrySent = true;

            // fetch more items once initial rendering is complete
            setTimeout(() => this._fetchMoreItems(), 0);
        }
    }

    private _performanceScenarioSplit(name: string) {
        this._perfTelemetryHelper.split(name);
    }
}

VssComponent.register("work-items-tab-content", WorkItemsTabContent);
